import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
    Sarlaft,
    SarlaftCallInfo,
    SarlaftComplaints,
    SarlaftDeclaration,
    SarlaftFinancial,
    SarlaftForm,
    SarlaftInsurance,
    SarlaftOperationInternational,
    SarlaftOperations,
    SarlaftPersonNatural,
    SarlaftSecurity,
    SarlaftSendEmail,
    SarlaftStateCase,
    SarlaftStrategicAlly,
    SarlaftSurvey,
    SarlaftTypePerson,
    SarlaftTypification,
    SarlaftVinculation,
    SarlaftVinculationExist,
} from '../../../../../shared/types/modules/sarlaft';
import { FormikProps } from 'formik';
import { cleanObj } from '../../../../../shared/utils/objects';
import { ToastSwal } from '../../../../components/general/toastSwals/toastSwal';
import { SARLAFT, SARLAFT_DETAIL } from '../../../../../routes/paths';
import {
    asyncCreateSarlaft,
    asyncGetSarlaft,
    asyncUpdateSarlaft,
    asyncGetSendEmail,
} from '../../../../../services/sarlaft.service';
import { ISelect, ParamEnum } from '../../../../../shared/types/shared';
import { asyncGetParamById } from '../../../../../services/param.service';
import { Account } from '../../../../../shared/types/modules/account';

export const useSarlaftForm = (account: Account | undefined) => {
    const navigate = useNavigate();
    const { idSarlaft } = useParams();
    const { idAccount } = useParams();
    const [sarlaft, setSarlaft] = useState<Sarlaft>();
    const [loading, setLoading] = useState(false);
    const [formikSarlaftCallInfo, setFormikSarlaftCallInfo] =
        useState<FormikProps<SarlaftCallInfo>>();
    const [formikSarlaftSecurity, setFormikSarlaftSecurity] =
        useState<FormikProps<SarlaftSecurity>>();
    const [formikSarlaftTypification, setFormikSarlaftTypification] =
        useState<FormikProps<SarlaftTypification>>();
    const [formikSarlaftVinculation, setFormikSarlaftVinculation] =
        useState<FormikProps<SarlaftVinculation>>();
    const [formikSarlaftVinculationExist, setFormikSarlaftVinculationExist] =
        useState<FormikProps<SarlaftVinculationExist>>();
    const [formikSarlaftTypePerson, setFormikSarlaftTypePerson] =
        useState<FormikProps<SarlaftTypePerson>>();
    const [formikSarlaftPersonNatural, setFormikSarlaftPersonNatural] =
        useState<FormikProps<SarlaftPersonNatural>>();
    const [formikSarlaftSurvey, setFormikSarlaftSurvey] =
        useState<FormikProps<SarlaftSurvey>>();
    const [formikSarlaftFinancial, setFormikSarlaftFinancial] =
        useState<FormikProps<SarlaftFinancial>>();
    const [
        formikSarlaftOperationInternational,
        setFormikSarlaftOperationInternational,
    ] = useState<FormikProps<SarlaftOperationInternational>>();
    const [formikSarlaftOperations, setFormikSarlaftOperations] =
        useState<FormikProps<SarlaftOperations>>();
    const [formikSarlaftInsurance, setFormikSarlaftInsurance] =
        useState<FormikProps<SarlaftInsurance>>();
    const [formikSarlaftComplaints, setFormikSarlaftComplaints] =
        useState<FormikProps<SarlaftComplaints>>();
    const [formikSarlaftForm, setFormikSarlaftForm] =
        useState<FormikProps<SarlaftForm>>();
    const [formikSarlaftDeclaration, setFormikSarlaftDeclaration] =
        useState<FormikProps<SarlaftDeclaration>>();
    const [formikSarlaftStateCase, setFormikSarlaftStateCase] =
        useState<FormikProps<SarlaftStateCase>>();
    const [formikSarlaftSendEmail, setFormikSarlaftSendEmail] =
        useState<FormikProps<SarlaftSendEmail>>();
    const [formikSarlaftStrategicAlly, setFormikSarlaftStrategicAlly] =
        useState<FormikProps<SarlaftStrategicAlly>>();
    const [statusValidation, setStatusValidation] = useState<ISelect>();

    const isSuccessfulValidation =
        statusValidation?.code ===
        `${ParamEnum.SARLAFT_STATUS_VALIDATION}_SUCCESSFUL_VALIDATION`;

    const isValidForm = isSuccessfulValidation
        ? formikSarlaftCallInfo?.isValid &&
          formikSarlaftSecurity?.isValid &&
          formikSarlaftTypification?.isValid &&
          formikSarlaftVinculation?.isValid &&
          formikSarlaftVinculationExist?.isValid &&
          formikSarlaftTypePerson?.isValid &&
          formikSarlaftPersonNatural?.isValid &&
          formikSarlaftSurvey?.isValid &&
          formikSarlaftFinancial?.isValid &&
          formikSarlaftOperationInternational?.isValid &&
          (formikSarlaftOperationInternational?.values.foreignCurrencyAccounts
              ? formikSarlaftOperations?.isValid ?? false
              : true) &&
          formikSarlaftInsurance?.isValid &&
          (formikSarlaftInsurance?.values.complaintsLastTwoYears
              ? formikSarlaftComplaints?.isValid ?? false
              : true) &&
          formikSarlaftForm?.isValid &&
          formikSarlaftDeclaration?.isValid &&
          formikSarlaftStateCase?.isValid &&
          formikSarlaftSendEmail?.isValid &&
          formikSarlaftStrategicAlly?.isValid
        : formikSarlaftCallInfo?.isValid &&
          formikSarlaftSecurity?.isValid &&
          formikSarlaftTypification?.isValid;

    const handleSubmit = async () => {
        await formikSarlaftOperationInternational?.submitForm();
        await formikSarlaftInsurance?.submitForm();
        if (
            formikSarlaftOperationInternational?.values.foreignCurrencyAccounts
        ) {
            await formikSarlaftOperations?.submitForm();
        }
        if (formikSarlaftInsurance?.values.complaintsLastTwoYears) {
            await formikSarlaftComplaints?.submitForm();
        }
        await formikSarlaftCallInfo?.submitForm();
        await formikSarlaftSecurity?.submitForm();
        await formikSarlaftTypification?.submitForm();
        await formikSarlaftVinculation?.submitForm();
        await formikSarlaftVinculationExist?.submitForm();
        await formikSarlaftTypePerson?.submitForm();
        await formikSarlaftPersonNatural?.submitForm();
        await formikSarlaftSurvey?.submitForm();
        await formikSarlaftFinancial?.submitForm();
        await formikSarlaftForm?.submitForm();
        await formikSarlaftDeclaration?.submitForm();
        await formikSarlaftStateCase?.submitForm();
        await formikSarlaftSendEmail?.submitForm();
        await formikSarlaftStrategicAlly?.submitForm();

        if (!isValidForm) return;

        setLoading(true);

        const data: Sarlaft = {};
        data.account = sarlaft?.account?.id || idAccount;
        data.email = account?.email || sarlaft?.account?.email;
        data.callInfo = cleanObj(formikSarlaftCallInfo?.values);
        data.security = cleanObj(formikSarlaftSecurity?.values);
        data.typification = cleanObj(formikSarlaftTypification?.values);
        data.entailment = cleanObj(formikSarlaftVinculation?.values);
        data.existingEntailment = cleanObj(
            formikSarlaftVinculationExist?.values
        );
        data.typePerson = cleanObj(formikSarlaftTypePerson?.values);
        data.naturalPerson = cleanObj(formikSarlaftPersonNatural?.values);
        data.survey = cleanObj(formikSarlaftSurvey?.values);
        data.financialInformation = cleanObj(formikSarlaftFinancial?.values);
        data.internationalOperationsActivities = cleanObj(
            formikSarlaftOperationInternational?.values
        );
        data.operations = cleanObj(formikSarlaftOperations?.values);
        data.insurance = cleanObj(formikSarlaftInsurance?.values);
        data.complaints = cleanObj(formikSarlaftComplaints?.values);
        data.form = cleanObj(formikSarlaftForm?.values);
        data.taxReturn = cleanObj(formikSarlaftDeclaration?.values);
        data.caseStatus = cleanObj(formikSarlaftStateCase?.values);
        data.intermediateForm = cleanObj(formikSarlaftSendEmail?.values);
        data.strategicAlly = cleanObj(formikSarlaftStrategicAlly?.values);

        let res: Sarlaft | undefined = undefined;

        try {
            if (sarlaft && sarlaft.id)
                res = await asyncUpdateSarlaft(sarlaft.id, data);
            else res = await asyncCreateSarlaft(data);
        } catch (error) {}

        if (res) {
            if (data.caseStatus?.caseStatus) {
                asyncGetParamById(data.caseStatus?.caseStatus).then((items) => {
                    if (
                        items?.code === `${ParamEnum.SARLAFT_STATUS}_UNUSUAL` &&
                        data.email
                    ) {
                        const taxReturn =
                            data.taxReturn?.lastYearTaxReturn ?? false;
                        asyncGetSendEmail(
                            `<${data.email}>`,
                            true,
                            taxReturn
                        ).then((res) => {
                            if (res) {
                                ToastSwal.fire({
                                    text: `Hemos enviado un correo solicitando información por ser una operación inusual`,
                                    icon: 'success',
                                    timer: 5000,
                                    position: 'center-end',
                                });
                            }
                        });
                    }
                });
            }

            ToastSwal.fire({
                text: `¡Felicidades! El SARLAFT se ${
                    sarlaft && sarlaft.id ? 'actualizo' : 'creó'
                } satisfactoriamente.`,
                icon: 'success',
                timer: 5000,
                position: 'center-end',
            });
            navigate(`${SARLAFT_DETAIL}/${res.id}`);
        } else {
            ToastSwal.fire({
                text: `Lo sentimos, no se pudo ${
                    sarlaft && sarlaft.id ? 'actualizar' : 'crear'
                } el SARLAFT. Por favor, asegúrate de ${
                    sarlaft && sarlaft.id
                        ? 'diligenciar los datos correctamente'
                        : 'que no exista previamente'
                } e intenta nuevamente más tarde.`,
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        }

        setLoading(false);
    };

    const handleClean = () => {
        formikSarlaftCallInfo?.resetForm();
        formikSarlaftSecurity?.resetForm();
        formikSarlaftTypification?.resetForm();
        formikSarlaftVinculation?.resetForm();
        formikSarlaftVinculationExist?.resetForm();
        formikSarlaftTypePerson?.resetForm();
        formikSarlaftPersonNatural?.resetForm();
        formikSarlaftSurvey?.resetForm();
        formikSarlaftFinancial?.resetForm();
        formikSarlaftOperationInternational?.resetForm();
        formikSarlaftOperations?.resetForm();
        formikSarlaftInsurance?.resetForm();
        formikSarlaftComplaints?.resetForm();
        formikSarlaftForm?.resetForm();
        formikSarlaftDeclaration?.resetForm();
        formikSarlaftStateCase?.resetForm();
        formikSarlaftSendEmail?.resetForm();
        formikSarlaftStrategicAlly?.resetForm();
    };

    const handleBack = () => {
        navigate(SARLAFT);
    };

    useEffect(() => {
        if (Boolean(idSarlaft?.trim())) {
            setLoading(true);
            asyncGetSarlaft(idSarlaft?.trim())
                .then((sarlaft) => {
                    if (!sarlaft || !sarlaft.id) navigate(SARLAFT);

                    [
                        sarlaft?.callInfo,
                        sarlaft?.security,
                        sarlaft?.typification,
                        sarlaft?.entailment,
                        sarlaft?.existingEntailment,
                        sarlaft?.typePerson,
                        sarlaft?.naturalPerson,
                        sarlaft?.survey,
                        sarlaft?.financialInformation,
                        sarlaft?.internationalOperationsActivities,
                        sarlaft?.operations,
                        sarlaft?.insurance,
                        sarlaft?.complaints,
                        sarlaft?.form,
                        sarlaft?.taxReturn,
                        sarlaft?.caseStatus,
                        sarlaft?.intermediateForm,
                        sarlaft?.strategicAlly,
                        sarlaft?.account,
                    ].forEach((item: any) => {
                        if (item)
                            Object.keys(item).forEach((key) => {
                                if (item?.[key] && (item?.[key] as ISelect)?.id)
                                    item[key] = (
                                        item[key] as ISelect
                                    )?.id.toString();
                            });
                    });

                    setSarlaft(sarlaft);
                })
                .finally(() => setLoading(false));
        }
    }, [idSarlaft]);

    useEffect(() => {
        asyncGetParamById(
            formikSarlaftSecurity?.values.statusValidation as string
        ).then((items) => {
            setStatusValidation(items);
        });
    }, [formikSarlaftSecurity?.values.statusValidation]);

    return {
        sarlaft,
        isSuccessfulValidation,
        loading,
        isValidForm,
        handleSubmit,
        handleClean,
        handleBack,
        formikSarlaftCallInfo,
        formikSarlaftSecurity,
        formikSarlaftTypification,
        formikSarlaftVinculation,
        formikSarlaftVinculationExist,
        formikSarlaftTypePerson,
        formikSarlaftPersonNatural,
        formikSarlaftSurvey,
        formikSarlaftFinancial,
        formikSarlaftOperationInternational,
        formikSarlaftOperations,
        formikSarlaftInsurance,
        formikSarlaftComplaints,
        formikSarlaftForm,
        formikSarlaftDeclaration,
        formikSarlaftStateCase,
        formikSarlaftSendEmail,
        formikSarlaftStrategicAlly,
        setFormikSarlaftCallInfo,
        setFormikSarlaftSecurity,
        setFormikSarlaftTypification,
        setFormikSarlaftVinculation,
        setFormikSarlaftVinculationExist,
        setFormikSarlaftTypePerson,
        setFormikSarlaftPersonNatural,
        setFormikSarlaftSurvey,
        setFormikSarlaftFinancial,
        setFormikSarlaftOperationInternational,
        setFormikSarlaftOperations,
        setFormikSarlaftInsurance,
        setFormikSarlaftComplaints,
        setFormikSarlaftForm,
        setFormikSarlaftDeclaration,
        setFormikSarlaftStateCase,
        setFormikSarlaftSendEmail,
        setFormikSarlaftStrategicAlly,
    };
};
